import { Box, Container, Divider, Theme, useMediaQuery } from '@mui/material';
import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, NavigateFunction } from 'react-router';
import { ActionPanel, BreadcrumbBar, Header, Home, LoadingScreen } from '.';
import { AppDispatch, AppState } from '../store';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Footer } from './Footer';
import { LandingPage } from './Landing/LandingPage';
import { ErrorDialog } from './AppError';
import { isMobile, GetToken, IsAppOpenedInIframe, showHeaderFooter } from '../services';
import { EUrlParams, EErrorTypes, ESessionStore, EEnvironments } from '../data/Constants';
import { ErrorPage } from './ErrorPage';
import { SessionStore } from '../services/SessionStore';
import { Logout } from './Logout';
import { setApplicationSettings } from '../store/states/ApplicationSettingsSlice';
import { appSettings } from '../settings';
import { ToastContainer } from 'react-toastify';

/**
 * Main component to handle the layout at the top level.
 * Sets the different routes.
 * Sets the current page size in the userSettingsState.
 * @returns {JSX.Element} a container with the UI components at top level
 */
function checkScope(navigate: NavigateFunction, token: string) {
  if (
    token &&
    SessionStore.get(ESessionStore.ConfigurationId) &&
    SessionStore.get(ESessionStore.Scope) === null
  ) {
    navigate('/Error', { replace: true, state: { type: EErrorTypes.NotAuthorized } });
  }
}

export function AppRouter() {
  let currentPageSize = {
    isExtraSmall: useMediaQuery(
      (theme: Theme) => theme.breakpoints.up('xs') && theme.breakpoints.down('sm')
    ),
    isSmall: useMediaQuery(
      (theme: Theme) => theme.breakpoints.up('xs') && theme.breakpoints.down('sm')
    ),
    isMedium: useMediaQuery(
      (theme: Theme) => theme.breakpoints.up('sm') && theme.breakpoints.down('md')
    ),
    isLarge: useMediaQuery(
      (theme: Theme) => theme.breakpoints.up('lg') && theme.breakpoints.down('xl')
    ),
    isExtraLarge: useMediaQuery((theme: Theme) => theme.breakpoints.up('xl')),
  };
  const mobilePageSize = {
    isExtraSmall: useMediaQuery(
      (theme: Theme) => theme.breakpoints.up('xs') && theme.breakpoints.down('sm')
    ),
    isSmall: useMediaQuery(
      (theme: Theme) => theme.breakpoints.up('sm') && theme.breakpoints.down('md')
    ),
    isMedium: useMediaQuery(
      (theme: Theme) => theme.breakpoints.up('md') && theme.breakpoints.down('lg')
    ),
    isLarge: useMediaQuery(
      (theme: Theme) => theme.breakpoints.up('lg') && theme.breakpoints.down('xl')
    ),
    isExtraLarge: useMediaQuery((theme: Theme) => theme.breakpoints.up('xl')),
  };
  if (isMobile()) {
    currentPageSize = mobilePageSize;
  }
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = GetToken();
  const dispatch = useDispatch<AppDispatch>();

  const pageSize = useSelector((state: AppState) => state.applicationSettings.pageSize);

  useEffect(() => {
    if (JSON.stringify(pageSize) === JSON.stringify(currentPageSize)) {
      return;
    }
    dispatch(setApplicationSettings({ pageSize: currentPageSize }));
  });

  useEffect(() => {
    checkScope(navigate, token);
  }, [token]);

  const showFooter = !(
    IsAppOpenedInIframe() ||
    showHeaderFooter() ||
    appSettings.Environment === EEnvironments.PROD
  );

  const commonRoutes = (
    <>
      <Route path="/Error" element={<ErrorPage />} />
      <Route path="/Logout" element={<Logout />} />
      <Route path="/notified" element={<h4>{t('successMessages.notifySuccessMessage')}</h4>} />
      <Route path="/closeTab" element={<h4>{t('successMessages.closeTabMessage')}</h4>} />
    </>
  );

  return (
    <Container maxWidth={false} className="appRouter root">
      <Header />
      <Box className="secondaryHeader">
        <BreadcrumbBar />
        <ActionPanel />
      </Box>
      <Divider className="divider" />
      <Box className="appRouterContent">
        <Routes>
          {location.search && location.search !== `?${EUrlParams.ConfigurationId}=null` ? (
            <>
              {commonRoutes}
              <Route path="/signin/oidc" element={<Home />} />
              <Route path="/signin/oidc-silent" element={<Home />} />
              <Route path="/landingPage" element={<LandingPage />} />
              <Route path="/" element={<Home />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : (
            <>
              {commonRoutes}
              <Route path="/landingPage" element={<LandingPage />} />
              <Route path="*" element={<Navigate to="/landingPage" replace />} />
            </>
          )}
        </Routes>
      </Box>
      <LoadingScreen />
      <ToastContainer
        position="bottom-center"
        autoClose={3000}
        hideProgressBar={true}
        closeOnClick={false}
        pauseOnHover
        theme="colored"
        draggable
      />
      <ErrorDialog />
      {showFooter && <Footer />}
    </Container>
  );
}
